import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Box,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserQuotePreview } from "../../../redux/slices/dashSlice";
import {
  PersonOutlined,
  EmailOutlined,
  PhoneOutlined,
  DescriptionOutlined,
  LocationCityOutlined,
  BusinessOutlined,
  FormatListNumberedOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import LoadingIndicator from "../../utils/LoadingIndicator";

const ClientPreview = forwardRef(({ search, role, user, from, to, month, year, selectedRegion, selectedState, selectedChannels }, ref) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userQuotePreview, totalCount, getStatus } = useSelector(
    (state) => state.dash
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(
      fetchUserQuotePreview({
        page: page + 1,
        rowsPerPage,
        search: search,
        role: role,
        user:  user,
        from: from,
        to: to,
        month: from || to ? null : month,
        year: from || to ? null : year,
        selectedRegion: selectedRegion,
        selectedState: selectedState,
        selectedChannels: selectedChannels
      })
    );
  }, [dispatch, page, rowsPerPage, search, role, from, to, user, month, year, selectedRegion, selectedState, selectedChannels]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useImperativeHandle(ref, () => ({
    downloadExcel: () => {
      if (!userQuotePreview || userQuotePreview.length === 0) return;

      const dataToExport = userQuotePreview.map((client, index) => ({
        "S.No.": index + 1,
        "User Name": client.username,
        Role: client.rolename,
        Email: client.email,
        Phone: client.phone,
        Quotations: client.project_count,
        Region: client.city,
        State: client.state,
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Clients");
      XLSX.writeFile(wb, "top_user_quotation.xlsx");
    },
  }));

  return (
    <>
      <LoadingIndicator show={getStatus === "pending"} />

      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-label="client table">
              <TableHead sx={{ background: "#04121f", color: "white" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <FormatListNumberedOutlined /> S. No.
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonOutlined /> User Name
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonOutlined /> Role
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <EmailOutlined /> Email
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneOutlined /> Phone
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DescriptionOutlined /> Quotations
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationCityOutlined /> Region
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessOutlined /> State
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userQuotePreview?.length > 0 ? (
                  userQuotePreview.map((client, index) => (
                    <TableRow
                      key={`${client.username}-${index}-${client.project_id}-${client.project_count}-${client.email}`}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#f9fafb" },
                        "&:hover": { backgroundColor: "#eef2ff" },
                        transition: "background-color 0.3s ease",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate("/admin/quotation-by-user", {
                          state: { id: client.user_id,user: user, role, from, to, month, year, selectedRegion, selectedState },
                        })
                      }
                    >
                      <TableCell sx={{ fontWeight: "bold", color: "#4c66f5" }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{client.username}</TableCell>
                      <TableCell>{client.rolename}</TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>{client.phone}</TableCell>
                      <TableCell>{client.project_count}</TableCell>
                      <TableCell>{client.city}</TableCell>
                      <TableCell>{client.state}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        sx={{ py: 2 }}
                      >
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, totalCount || 0]}
            component="div"
            count={totalCount || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </>
  );
});

export default ClientPreview;
